/* eslint-disable react/jsx-props-no-spreading */
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  ListItem,
  ListItemAvatar,
  makeStyles,
  Slide,
  Typography,
  useMediaQuery,
  useTheme
} from "@material-ui/core";
import { Delete, Edit, Reply, Share } from "@material-ui/icons";
import { Formik } from "formik";
import { filter as _filter, take as _take } from "lodash";
import React, { forwardRef, useState } from "react";
import { useIntl } from "react-intl";
import { DeleteDialog, Flaticon, NoQAData } from "../../components";
import { SubmitButton, TextField } from "../../components/formik";
import { deleteQuestionAnswer, updateQuestionAnswer } from "../../ducks/actions";
import { createProfileQuestion } from "../../ducks/actions/profiles";
import { useActionRequest, useUserMe } from "../../hooks";
import EditQuestionAnswerDialog from "../eposters/EditQuestionAnswerDialog";

const useStyles = makeStyles(theme => ({
  gridContainer: {
    margin: "0.5rem 0 0 0",
    gap: "1rem"
  },
  dialogPaper: {
    maxHeight: "80vh"
  },
  gridItemQA: {
    margin: "0.3rem 0"
  },
  formSubmitButton: {
    marginTop: "16px",
    marginBottom: "8px"
  },

  qaActions: {
    "& > *": {
      marginRight: theme.spacing(1)
    }
  },
  questionFormItem: {
    [theme.breakpoints.down("md")]: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1)
    }
  }
}));

const Transition = forwardRef((props, ref) => {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function QAModal({
  questionsAndAnswers,
  profileId,
  teacherProfile,
  isOpen,
  handleClose
}) {
  const [showAllQA, setShowAllQA] = useState(false);
  const [selectedProfileQA, setSelectedProfileQA] = useState(null);
  const [deleteProfileQaModalData, setDeleteProfileQaModalData] = useState({
    isOpen: false,
    questionId: null
  });

  const classes = useStyles();
  const theme = useTheme();
  const intl = useIntl();
  const { user } = useUserMe();

  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const publishedQas = _filter(questionsAndAnswers, qa => qa.published);
  const unpublishedQas = _filter(questionsAndAnswers, qa => !qa.published && !!qa.answer);
  const unansweredQas = _filter(questionsAndAnswers, qa => !qa.answer);

  const createQuestionAnswerRequest = useActionRequest({
    action: createProfileQuestion,
    successMessage:
      "Your question has been addressed to the author and it will be visible as soon as the author will reply"
  });

  const shareModuleQARequest = useActionRequest({
    action: updateQuestionAnswer,
    successMessage: "The answer has been updated"
  });

  const deleteQuestionAnswerRequest = useActionRequest({
    action: deleteQuestionAnswer,
    successMessage: "The answer has been deleted"
  });

  const onQuestionAnswerCreate = ({ question }, { resetForm }) => {
    createQuestionAnswerRequest({ profileId, question });
    resetForm();
  };

  const confirmDeleteQa = ({ id }) => () => {
    setDeleteProfileQaModalData({ isOpen: false, questionId: null });
    deleteQuestionAnswerRequest({ id });
  };

  const onQuestionAnswerShared = ({ questionAnswer }) => () => {
    shareModuleQARequest({ id: questionAnswer.id, questionAnswer });
  };

  const onEditModuleQADialogOpen = ({ questionAnswer }) => () => {
    setSelectedProfileQA(questionAnswer);
  };

  const onEditModuleQADialogClose = () => {
    setSelectedProfileQA(null);
  };

  const showAllQuestion = () => {
    setShowAllQA(true);
  };

  const openDeleteQaModal = ({ id }) => () => {
    setDeleteProfileQaModalData({ isOpen: true, questionId: id });
  };

  const closeDeleteQaModal = () => {
    setDeleteProfileQaModalData({ isOpen: false, questionId: null });
  };

  return (
    <Dialog
      fullScreen={fullScreen}
      fullWidth
      maxWidth="lg"
      classes={{ paper: classes.dialogPaper }}
      open={isOpen}
      TransitionComponent={Transition}
      onClose={handleClose}
    >
      <DialogContent>
        {teacherProfile.id === user.profile?.id ? (
          <>
            <Grid container id="questionAndAnswers" className={classes.gridContainer}>
              <Grid item xs={12}>
                <Typography variant="h5">
                  {intl.formatMessage({
                    id: "Pages.CourseModule.QuestionsAndAnswers",
                    defaultMessage: "Questions and Answers"
                  })}
                </Typography>
              </Grid>
              <NoQAData data={publishedQas}>
                <Grid container className={classes.gridContainer}>
                  {(showAllQA ? publishedQas : _take(publishedQas, 2)).map(publishedQa => (
                    <Grid
                      item
                      container
                      key={publishedQa.id}
                      xs={12}
                      className={classes.gridItemQA}
                    >
                      <ListItem>
                        <ListItemAvatar>
                          <Avatar src={publishedQa.profile?.avatarThumb}>
                            <Flaticon icon="question" />
                          </Avatar>
                        </ListItemAvatar>
                        <Typography variant="body1">
                          {`${publishedQa.profile?.firstName} ${publishedQa.profile?.lastName}:`}{" "}
                          <strong>{publishedQa.question}</strong>
                        </Typography>
                      </ListItem>
                      <ListItem>
                        <ListItemAvatar>
                          <Avatar src={publishedQa.answerer?.avatarThumb} />
                        </ListItemAvatar>
                        <Typography variant="body1">
                          <i>{publishedQa.answer}</i>
                        </Typography>
                      </ListItem>
                      <ListItem className={classes.qaActions}>
                        <Button
                          variant="contained"
                          disableElevation
                          color="primary"
                          onClick={onEditModuleQADialogOpen({
                            questionAnswer: publishedQa
                          })}
                        >
                          <Edit />
                          {intl.formatMessage({
                            id: "Pages.CourseModule.Edit",
                            defaultMessage: "Edit"
                          })}
                        </Button>
                        <Button
                          variant="contained"
                          disableElevation
                          color="primary"
                          onClick={openDeleteQaModal({ id: publishedQa.id })}
                        >
                          <Delete />
                          {intl.formatMessage({
                            id: "Pages.CourseModule.Delete",
                            defaultMessage: "Delete"
                          })}
                        </Button>
                      </ListItem>
                    </Grid>
                  ))}
                  {!showAllQA && publishedQas.length > 2 && (
                    <Button variant="contained" color="primary" onClick={showAllQuestion}>
                      Show {publishedQas.length - 2} more question
                    </Button>
                  )}
                </Grid>
              </NoQAData>
            </Grid>
            <Divider />
            <Grid container id="questionsWithoutAnswer" className={classes.gridContainer}>
              <Grid item xs={12}>
                <Typography variant="h5">
                  {intl.formatMessage({
                    id: "Pages.CourseModule.QuestionsWithoutAnswer",
                    defaultMessage: "Questions Without Answers"
                  })}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">
                  {intl.formatMessage({
                    id: "Pages.CourseModule.QuestionsWithoutAnswerHelper",
                    defaultMessage:
                      "Questions and comments sent to you will be publicly visible only if and when you decide to answer them."
                  })}
                </Typography>
              </Grid>

              <NoQAData data={unansweredQas}>
                <Grid container className={classes.gridContainer}>
                  {unansweredQas.map(({ id, question, profile, answer, published }) => (
                    <Grid item container key={id} xs={12} className={classes.gridItemQA}>
                      <ListItem>
                        <ListItemAvatar>
                          <Avatar src={profile?.avatarThumb}>
                            <Flaticon icon="question" />
                          </Avatar>
                        </ListItemAvatar>
                        <Typography variant="body1">
                          {`${profile?.firstName} ${profile?.lastName}:`}{" "}
                          <strong>{question}</strong>
                        </Typography>
                      </ListItem>
                      <ListItem className={classes.qaActions}>
                        <Button
                          variant="contained"
                          disableElevation
                          color="primary"
                          onClick={onEditModuleQADialogOpen({
                            questionAnswer: { id, question, answer, published }
                          })}
                        >
                          <Reply />
                          {intl.formatMessage({
                            id: "Pages.CourseModule.Reply",
                            defaultMessage: "Reply"
                          })}
                        </Button>
                        <Button
                          variant="contained"
                          disableElevation
                          color="primary"
                          onClick={openDeleteQaModal({ id })}
                        >
                          <Delete />
                          {intl.formatMessage({
                            id: "Pages.CourseModule.Delete",
                            defaultMessage: "Delete"
                          })}
                        </Button>
                      </ListItem>
                    </Grid>
                  ))}
                </Grid>
              </NoQAData>
            </Grid>
            <Divider />
            <Grid container id="unpublishedQuestionsAndAnswers" className={classes.gridContainer}>
              <Grid item xs={12}>
                <Typography variant="h5">
                  {intl.formatMessage({
                    id: "Pages.CourseModule.UnpublishedQuestionsAndAnswers",
                    defaultMessage: "Unpublished Questions And Answers"
                  })}
                </Typography>
              </Grid>

              <NoQAData data={unpublishedQas}>
                <Grid container className={classes.gridContainer}>
                  {unpublishedQas.map(unpublishedQa => (
                    <Grid
                      item
                      container
                      key={unpublishedQa.id}
                      xs={12}
                      className={classes.gridItemQA}
                    >
                      <ListItem>
                        <ListItemAvatar>
                          <Avatar src={unpublishedQa.profile?.avatarThumb}>
                            <Flaticon icon="question" />
                          </Avatar>
                        </ListItemAvatar>
                        <Typography variant="body1">
                          {`${unpublishedQa.profile?.firstName} ${unpublishedQa.profile?.lastName}:`}{" "}
                          <strong>{unpublishedQa.question}</strong>
                        </Typography>
                      </ListItem>
                      <ListItem>
                        <ListItemAvatar>
                          <Avatar src={unpublishedQa.answerer?.avatarThumb} />
                        </ListItemAvatar>
                        <Typography variant="body1">
                          <i>{unpublishedQa.answer}</i>
                        </Typography>
                      </ListItem>
                      <ListItem className={classes.qaActions}>
                        <Button
                          variant="contained"
                          disableElevation
                          color="primary"
                          onClick={onQuestionAnswerShared({
                            questionAnswer: {
                              ...unpublishedQa,
                              published: true
                            }
                          })}
                        >
                          <Share />
                          {intl.formatMessage({
                            id: "Pages.CourseModule.Share",
                            defaultMessage: "Publish"
                          })}
                        </Button>
                        <Button
                          variant="contained"
                          disableElevation
                          color="primary"
                          onClick={onEditModuleQADialogOpen({
                            questionAnswer: unpublishedQa
                          })}
                        >
                          <Edit />
                          {intl.formatMessage({
                            id: "Pages.CourseModule.Edit",
                            defaultMessage: "Edit"
                          })}
                        </Button>
                        <Button
                          variant="contained"
                          disableElevation
                          color="primary"
                          onClick={openDeleteQaModal({ id: unpublishedQa.id })}
                        >
                          <Delete />
                          {intl.formatMessage({
                            id: "Pages.CourseModule.Delete",
                            defaultMessage: "Delete"
                          })}
                        </Button>
                      </ListItem>
                    </Grid>
                  ))}
                </Grid>
              </NoQAData>
            </Grid>
          </>
        ) : (
          <Grid container id="questionAndAnswers" className={classes.gridContainer}>
            <Grid item xs={12}>
              <Typography variant="h5">
                {intl.formatMessage({
                  id: "Pages.CourseModule.QuestionsAndAnswers",
                  defaultMessage: "Questions and Answers"
                })}
              </Typography>
            </Grid>

            <Formik initialValues={{ question: "" }} onSubmit={onQuestionAnswerCreate}>
              <Grid container justify="space-between">
                <Grid item xs={12} md={9} className={classes.questionFormItem}>
                  <TextField
                    name="question"
                    variant="outlined"
                    fullWidth
                    label={intl.formatMessage({
                      id: "Pages.CourseModule.AddQuestion",
                      defaultMessage: "Add a question"
                    })}
                  />
                </Grid>
                <Grid item xs={12} md={2} className={classes.questionFormItem}>
                  <SubmitButton
                    className={classes.formSubmitButton}
                    variant="contained"
                    color="primary"
                    fullWidth
                    defaultEnabled
                  >
                    {intl.formatMessage({
                      id: "Pages.CourseModule.SendQuestion",
                      defaultMessage: "Send Question"
                    })}
                  </SubmitButton>
                </Grid>
              </Grid>
            </Formik>

            <NoQAData data={publishedQas}>
              <Grid container className={classes.gridContainer}>
                {(showAllQA ? publishedQas : _take(publishedQas, 2)).map(
                  ({ id, question, answer, profile, answerer }) => (
                    <Grid item container key={id} xs={12} className={classes.gridItemQA}>
                      <ListItem>
                        <ListItemAvatar>
                          <Avatar src={profile?.avatarThumb}>
                            <Flaticon icon="question" />
                          </Avatar>
                        </ListItemAvatar>
                        <Typography variant="body1">
                          {`${profile?.firstName} ${profile?.lastName}:`}{" "}
                          <strong>{question}</strong>
                        </Typography>
                      </ListItem>
                      <ListItem>
                        <ListItemAvatar>
                          <Avatar src={answerer?.avatar_thumb} />
                        </ListItemAvatar>
                        <Typography variant="body1">
                          <i>{answer}</i>
                        </Typography>
                      </ListItem>
                    </Grid>
                  )
                )}
                {!showAllQA && publishedQas.length > 2 && (
                  <Button variant="contained" color="primary" onClick={showAllQuestion}>
                    Show {publishedQas.length - 2} more question
                  </Button>
                )}
              </Grid>
            </NoQAData>
          </Grid>
        )}
        {selectedProfileQA && (
          <EditQuestionAnswerDialog
            questionAnswer={selectedProfileQA}
            onClose={onEditModuleQADialogClose}
          />
        )}

        {deleteProfileQaModalData.isOpen && (
          <DeleteDialog
            id={deleteProfileQaModalData.questionId}
            onDelete={confirmDeleteQa({ id: deleteProfileQaModalData.questionId })}
            onClose={closeDeleteQaModal}
          >
            <DialogTitle>
              {intl.formatMessage({
                id: "Pages.Teacher.DeleteDialog.Title",
                defaultMessage: "Are you sure you want to cancel this question?"
              })}
            </DialogTitle>
          </DeleteDialog>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Chiudi
        </Button>
      </DialogActions>
    </Dialog>
  );
}
